import { Accordion, Box, Group, List, Stack, Text } from "@mantine/core";

import type { VhdpPlanData } from "./vhdpPlanData";

import styles from "./PlanTab.module.css";

export interface PlanTabProps {
    data: VhdpPlanData;
}

/**
 * "Plan details" — an at-a-glance price/benefit summary over an accordion of the fine print
 * (price & billing, coverage & eligibility, claims, support). Presentational; driven by `data`.
 */
export const PlanTab = ({ data }: PlanTabProps) => {
    const { atAGlance, sections, meta } = data;

    return (
        <Stack gap={20} className={styles.root}>
            <section>
                <Text component="h3" className={styles.sectionTitle}>
                    At a glance
                </Text>
                <Group align="flex-start" wrap="nowrap" gap={32} mt={12}>
                    <Box className={styles.priceBlock}>
                        <Text className={styles.priceLabel}>Monthly price</Text>
                        <Text className={styles.price}>{atAGlance.monthlyPrice}</Text>
                        <Text className={styles.priceNote}>{atAGlance.priceNote}</Text>
                    </Box>
                    <List spacing={8} className={styles.highlights}>
                        {atAGlance.highlights.map((h) => (
                            <List.Item key={h.label}>
                                {h.label}
                                {h.value ? <span className={styles.highlightValue}> {h.value}</span> : null}
                            </List.Item>
                        ))}
                    </List>
                </Group>
            </section>

            <Accordion
                variant="contained"
                radius="md"
                chevronPosition="right"
                classNames={{ control: styles.control, chevron: styles.chevron }}
            >
                {sections.map((section) => (
                    <Accordion.Item key={section.id} value={section.id}>
                        <Accordion.Control>
                            <Text className={styles.controlLabel}>{section.title}</Text>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Stack gap={10}>
                                {section.rows.map((row) => (
                                    <Group key={row.label} align="flex-start" wrap="nowrap" gap={16}>
                                        <Text className={styles.rowLabel}>{row.label}</Text>
                                        <Text className={styles.rowValue}>{row.value}</Text>
                                    </Group>
                                ))}
                            </Stack>
                        </Accordion.Panel>
                    </Accordion.Item>
                ))}
            </Accordion>

            <Text className={styles.reference}>
                {meta.referenceLabel} • {meta.version}
            </Text>
        </Stack>
    );
};
